import { FC, useMemo, useState } from 'react';
import { EndUserModal } from '../../../../components/EndUserModal';
import { EndUserTypography } from '../../../../components/EndUserTypography';
import { Form } from 'antd';
import { EndUserContactForm } from '../ContactForm';
import { useResetFormOnCloseModal } from '../../../../lib/hooks/useResetFormOnCloseModal';
import { ProductT } from '../../../../lib/types/product';
import { useAppDispatch, useAppSelector } from '../../../../lib/store/hooks';
import { useSendContactFormMutation } from '../../../../lib/store/api/endUser';
import { SuccessStateModal } from '../SuccessStateModal';
import { ButtonFormType, FormItemName } from '../../../../lib/models/Form';
import { sendDataToAnalytics } from '../../../../lib/utils/sendDataToAnalytics';
import { SectionName } from '../../../../lib/models/Section';
import { ContactFormBody, FormTypeProduct } from '../../../../lib/types/endUser';
import { useTranslation } from 'react-i18next';

import styles from './index.module.scss';
import { setSelectedProductButton } from '../../../../lib/store/slices/ProductFormSlice';

const contactForm = 'endUser.contactForm';

const getTextWithCompany = (text: string, companyName: string) => {
  if (!text) {
    return '';
  }

  return text
    .replace('$companyName', companyName)
    .split('\n')
    .map((item) => (
      <>
        {item}
        <br />
      </>
    ));
};

type Props = {
  isOpen: boolean;
  onClose: () => void;
  products: ProductT[] | null;
  isWishlist?: boolean;
  fromFooter?: boolean;
};

export const EndUserContactFormModal: FC<Props> = ({
  products: productsList,
  isOpen,
  onClose,
  fromFooter,
}) => {
  const dispatch = useAppDispatch();
  const [form] = Form.useForm();
  const { t } = useTranslation();

  const [isSuccessModalOpen, setIsSuccessModalOpen] = useState(false);
  const { tenantAndProject, isGuestMode, sections, demandArea } = useAppSelector(
    ({ endUser }) => endUser,
  );
  const { productButtonSelected } = useAppSelector(({ productForm }) => productForm);
  const clientName = demandArea?.tenant?.clientName;

  const [sendContactForm, { isLoading }] = useSendContactFormMutation();
  const userEmail = form.getFieldValue(FormItemName.EMAIL);
  const currentSectionContent = sections?.[SectionName.CONTACT_FORM];

  const [isChecked, setIsChecked] = useState(false);

  useResetFormOnCloseModal({ form, isOpen });

  const isCoverageIndicationForm = useMemo(() => {
    return productButtonSelected === ButtonFormType.COVERAGE_INDICATION;
  }, [productButtonSelected]);

  const isRequestProductSwitchForm = useMemo(() => {
    return productButtonSelected === ButtonFormType.REQUEST_PRODUCT_SWITCH;
  }, [productButtonSelected]);

  const buttonFormType = useMemo(() => {
    if (fromFooter) {
      return FormTypeProduct.FOOTER_CONTACT;
    } else if (isCoverageIndicationForm) {
      return FormTypeProduct.COVERAGE_INDICATION;
    } else if (isRequestProductSwitchForm) {
      return FormTypeProduct.REQUEST_PRODUCT_SWITCH;
    }
    return FormTypeProduct.PRODUCT_CONTACT;
  }, [fromFooter, isCoverageIndicationForm, isRequestProductSwitchForm]);

  const toggleCheckbox = () => {
    setIsChecked((prevState) => !prevState);
  };

  const handleClose = () => {
    dispatch(setSelectedProductButton(null));
    onClose();
  };

  const handleSubmit = async (values: Omit<ContactFormBody, 'products'>) => {
    const tenantId = tenantAndProject?.tenantId;
    const projectId = tenantAndProject?.projectId;

    const products = productsList?.map((item) => item.id);

    try {
      await sendContactForm({
        isGuestMode,
        tenantId: String(tenantId),
        projectId: Number(projectId),
        body: {
          ...values,
          buttonFormType: buttonFormType,
          products,
        },
      }).unwrap();

      setIsSuccessModalOpen(true);

      sendDataToAnalytics(
        'Interesse für vorgemerkte Produkte',
        'E-Mail senden',
        'User möchte gemerkte Produkte per E-Mail erhalten',
      );
    } catch (error) {
      console.log(error);
    }
  };

  const handleClickSubmit = () => {
    form.submit();
  };

  const handleCloseModals = () => {
    sendDataToAnalytics('Contact form success');

    onClose();
    setIsSuccessModalOpen(false);
  };

  const subtitle = useMemo(() => {
    if (isCoverageIndicationForm) {
      return currentSectionContent?.subtitle2;
    } else if (isRequestProductSwitchForm) {
      return currentSectionContent?.subtitle4 || '';
    } else if (fromFooter) {
      return currentSectionContent?.subtitle3;
    } else {
      return currentSectionContent?.subtitle;
    }
  }, [
    currentSectionContent?.subtitle,
    currentSectionContent?.subtitle2,
    currentSectionContent?.subtitle3,
    currentSectionContent?.subtitle4,
    fromFooter,
    isCoverageIndicationForm,
    isRequestProductSwitchForm,
  ]);

  const text = useMemo(() => {
    if (isCoverageIndicationForm) {
      return currentSectionContent?.text2;
    } else if (isRequestProductSwitchForm) {
      return getTextWithCompany(currentSectionContent?.text4 || '', String(clientName));
    } else if (fromFooter) {
      return currentSectionContent?.text3;
    } else {
      return currentSectionContent?.text;
    }
  }, [
    isCoverageIndicationForm,
    isRequestProductSwitchForm,
    fromFooter,
    currentSectionContent?.text2,
    currentSectionContent?.text4,
    currentSectionContent?.text3,
    currentSectionContent?.text,
    clientName,
  ]);

  return !isSuccessModalOpen ? (
    <EndUserModal
      open={isOpen}
      titleProps={{ title: currentSectionContent?.title, subTitle: subtitle }}
      cancelButtonProps={{ onClick: handleClose }}
      actionButtonProps={{
        onClick: handleClickSubmit,
        loading: isLoading,
        disabled: (isCoverageIndicationForm || isRequestProductSwitchForm) && !isChecked,
      }}
      cancelText={t(`${contactForm}.cancelText`)}
      okText={t(`${contactForm}.okText`)}
    >
      <EndUserTypography type="body" component="Text" className={styles.text}>
        {text}
      </EndUserTypography>
      <EndUserContactForm
        form={form}
        onFinish={handleSubmit}
        isChecked={isChecked}
        toggleCheckbox={toggleCheckbox}
        formType={buttonFormType}
      />
    </EndUserModal>
  ) : (
    <SuccessStateModal
      isOpen={isSuccessModalOpen}
      onClose={handleCloseModals}
      userEmail={userEmail}
    />
  );
};
